import React, { useEffect, useMemo } from 'react';
import { Box, Button, Divider, Grid, IconButton } from '@mui/material';
import { Controller, useForm, useFieldArray, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import { DeleteOutline } from '@mui/icons-material';
import BaseAutocomplete from 'components/Shared/Base/BaseAutocomplete';
import BaseSelect from 'components/Shared/Base/BaseSelect';
import LoadingWrapper from 'components/Shared/LoadingWrapper';

import InnerShadowBox from 'components/Shared/InnerShadowBox';
import { useAuth } from 'hooks';
import { useGetDDLSystemVendorUserListQuery, useLazyListShipmentListForBulkActionsQuery } from 'store/services/shipmentActionApi';
import { useNavigate } from 'react-router-dom';
import { formHookInputHelper } from 'utils/formHookField';
import RichTextEditor from "components/Shared/RichTextEditor";


const INITIAL_ACTION = {
  actionType: '',
  whoCanClose: '',
  status: 'Open',
  description: '',
  responsible: null,
  systemType: '',
  response: '',
  shipmentId: null,
  vendor: null,
};

const INITIAL_VALUE = {
  actions: [INITIAL_ACTION],
};

const ShipmentActionBulkForm = ({ isBusy = false, onSubmit = () => { } }) => {
  const { isVendor, loggedInUser, viewEntity } = useAuth();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: INITIAL_VALUE,
    mode: 'onChange',
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'actions',
  });

  const watchedActions = useWatch({ control, name: 'actions' });
  const systemTypes = useMemo(() => watchedActions.map((action) => action.systemType), [watchedActions]);

  const actionTypeEnums = ['Approval', 'Request', 'Info'];
  const systemTypeEnums = ['Internal', 'External'];
  const statusEnums = ['Open', 'Read', 'Closed'];

  // Fetch shipments
  const [fetchShipments, { data: shipmentRes, isLoading: loadingShipments, isFetching: fetchingShipments }] =
    useLazyListShipmentListForBulkActionsQuery();
  console.log(shipmentRes, "shipmentRes");

  // Map shipments for autocomplete - include vendor information
  const _mappedShipments = useMemo(() => {
    if (!shipmentRes?.data) return [];
    return shipmentRes.data.map((shipment) => ({
      _id: shipment._id,
      shipmentId: shipment.shipmentId,
      vendor: shipment.vendor, // Include vendor information
    }));
  }, [shipmentRes]);

  // Fetch shipments on mount
  useEffect(() => {
    fetchShipments({});
  }, [fetchShipments]);

  // Get whoCanClose options based on systemType
  const getWhoCanCloseEnums = (systemType) => {
    if (isVendor) {
      return ['Myself', 'Admin'];
    }
    return systemType === 'Internal' ? ['Admin'] : systemType === 'External' ? ['Admin', 'Vendor'] : [];
  };

  // Fetch responsible users
  const { data: userList = [], isLoading: loadingUsers, isFetching: fetchingUsers, error: userListError } =
    useGetDDLSystemVendorUserListQuery(
      {
        active: 'true',
        type: systemTypes.some((type) => type) ? systemTypes[0] : undefined,
      },
      { skip: !systemTypes.some((type) => type) }
    );

  const _mappedUsers = useMemo(
    () => userList?.map((el) => ({ label: el?.name, id: el?._id })) || [],
    [userList]
  );

  // Handle user list fetch errors
  useEffect(() => {
    if (userListError) {
      toast.error('Failed to load responsible users. Please try again.');
    }
  }, [userListError]);

  // Auto-populate responsible and whoCanClose
  useEffect(() => {
    systemTypes.forEach((systemType, index) => {
      if (systemType) {
        const currentResponsible = watch(`actions.${index}.responsible`);
        const currentWhoCanClose = watch(`actions.${index}.whoCanClose`);

        // Only set responsible if not already set
        if (loggedInUser && !currentResponsible) {
          const responsibleUser = {
            label: loggedInUser.name,
            id: loggedInUser._id,
          };
          setValue(`actions.${index}.responsible`, responsibleUser, { shouldValidate: true });
        }

        // Only set whoCanClose if not already set
        if (!isVendor && !currentWhoCanClose) {
          const defaultWhoCanClose = systemType === 'Internal' ? 'Admin' : 'Vendor';
          setValue(`actions.${index}.whoCanClose`, defaultWhoCanClose, { shouldValidate: true });
        } else if (isVendor && !currentWhoCanClose) {
          setValue(`actions.${index}.whoCanClose`, 'Myself', { shouldValidate: true });
        }
      }
    });
  }, [systemTypes, loggedInUser, setValue, isVendor, watch]);

  // Handle form submission
  const handleSubmitShipmentActions = async (data) => {
    // Validate shipments
    for (let i = 0; i < data.actions.length; i++) {
      const action = data.actions[i];
      if (!action.shipmentId?._id) {
        toast.error(`Please select a shipment for Action ${i + 1}`);
        return;
      }
    }

    // Create payload - include vendor from shipment
    const actionPayloads = data.actions.map((action) => ({
      actionType: action.actionType,
      whoCanClose: action.whoCanClose,
      status: action.status || 'Open',
      description: action.description,
      response: action.response || '',
      responsible: action.responsible?.id,
      vendor: action.shipmentId?.vendor, // Extract vendor from selected shipment
      shipmentId: action.shipmentId._id,
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
      systemType: isVendor ? 'External' : action.systemType || '',
    }));

    await onSubmit(actionPayloads);
  };

  const addNewAction = () => {
    append(INITIAL_ACTION);
  };

  // Handle shipment selection and auto-populate vendor
  const handleShipmentChange = (index, selectedShipment) => {
    // Set the shipment
    setValue(`actions.${index}.shipmentId`, selectedShipment, { shouldValidate: true });

    // Auto-populate vendor if shipment has vendor information
    if (selectedShipment?.vendor) {
      setValue(`actions.${index}.vendor`, selectedShipment.vendor, { shouldValidate: true });
    }
  };

  return (
    <LoadingWrapper loading={isBusy}>
      <InnerShadowBox sx={{ padding: 2, }}>
        {fields.map((field, index) => (
          <Grid
            container
            spacing={3}
            key={field.id}
            sx={{ mb: 3, p: 2, borderBottom: '1px solid #e5e7eb',}}
          >
            <Grid item xs={12}>
              <Box sx={{ fontSize: '0.95rem', fontWeight: 600, mb: 1 }}>
                Action {index + 1}
              </Box>
            </Grid>

            {/* Shipment Selection */}
            <Grid item xs={12} md={4}>
              <Controller
                name={`actions.${index}.shipmentId`}
                control={control}
                rules={{ required: 'Please select a shipment.' }}
                render={({ field }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Shipment"
                    size="small"
                    loading={loadingShipments || fetchingShipments}
                    loadingText="Loading Shipments..."
                    options={_mappedShipments}
                    value={field.value || null}
                    onChange={(_, data) => {
                      field.onChange(data);
                      handleShipmentChange(index, data); // Handle vendor auto-population
                    }}
                    getOptionLabel={(option) => option?.shipmentId || ''}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    error={!!errors?.actions?.[index]?.shipmentId}
                    helperText={errors?.actions?.[index]?.shipmentId?.message}
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem', borderRadius: '8px' } }}
                  />
                )}
              />
            </Grid>

            {!isVendor && (
              <Grid item xs={12} md={4}>
                <Controller
                  name={`actions.${index}.systemType`}
                  control={control}
                  rules={{ required: 'Please select System Type.' }}
                  render={(props) => (
                    <BaseSelect
                      {...formHookInputHelper(props)}
                      fullWidth
                      required
                      label="Type"
                      size="small"
                      options={systemTypeEnums}
                      error={!!errors?.actions?.[index]?.systemType}
                      helperText={errors?.actions?.[index]?.systemType?.message}
                      sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem', borderRadius: '8px' } }}
                    />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={12} md={4}>
              <Controller
                name={`actions.${index}.actionType`}
                control={control}
                rules={{ required: 'Please select an Action Type.' }}
                render={(props) => (
                  <BaseSelect
                    {...formHookInputHelper(props)}
                    fullWidth
                    required
                    label="Action Type"
                    size="small"
                    options={actionTypeEnums}
                    error={!!errors?.actions?.[index]?.actionType}
                    helperText={errors?.actions?.[index]?.actionType?.message}
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem', borderRadius: '8px' } }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name={`actions.${index}.whoCanClose`}
                control={control}
                rules={{ required: 'Please select Who Can Close.' }}
                render={(props) => (
                  <BaseSelect
                    {...formHookInputHelper(props)}
                    fullWidth
                    required
                    label="Who Can Close"
                    size="small"
                    options={getWhoCanCloseEnums(watchedActions[index]?.systemType)}
                    error={!!errors?.actions?.[index]?.whoCanClose}
                    helperText={errors?.actions?.[index]?.whoCanClose?.message}
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem', borderRadius: '8px' } }}
                  />
                )}
              />
            </Grid>

            {!isVendor && (
              <Grid item xs={12} md={4}>
                <Controller
                  name={`actions.${index}.responsible`}
                  control={control}
                  rules={{ required: 'Please select a Responsible User.' }}
                  render={({ field }) => (
                    <BaseAutocomplete
                      fullWidth
                      required
                      label="Responsible"
                      size="small"
                      loading={loadingUsers || fetchingUsers}
                      loadingText="Loading Responsible Users..."
                      options={_mappedUsers}
                      value={field.value || null}
                      onChange={(_, data) => field.onChange(data)}
                      getOptionLabel={(option) => option?.label || ''}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      error={!!errors?.actions?.[index]?.responsible}
                      helperText={errors?.actions?.[index]?.responsible?.message}
                      sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem', borderRadius: '8px' } }}
                    />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={12} md={4}>
              <Controller
                name={`actions.${index}.status`}
                control={control}
                rules={{ required: 'Please select status.' }}
                render={(props) => (
                  <BaseSelect
                    {...formHookInputHelper(props)}
                    fullWidth
                    label="Status"
                    size="small"
                    disabled
                    options={statusEnums}
                    error={!!errors?.actions?.[index]?.status}
                    helperText={errors?.actions?.[index]?.status?.message}
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem', borderRadius: '8px' } }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <div style={{ color: "gray", fontSize: 15, marginBottom: ".75rem" }}>
                Description
              </div>
              <Controller
                name={`actions.${index}.description`} // Fix: Use indexed name
                control={control}
                render={({ field: { onChange } }) => (
                  <RichTextEditor
                    initialValue={"<p>Enter description</p>"}
                    onChange={(value) => {
                      const plainText = value.replace(/<[^>]+>/g, "");
                      onChange(plainText);
                    }}
                    error={!!errors?.actions?.[index]?.description}
                    helperText={errors?.actions?.[index]?.description?.message}
                  />
                )}
              />
            </Grid>

            {fields.length > 1 && (
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton
                  onClick={() => remove(index)}
                  sx={{ color: '#ef4444' }}
                  size="small"
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Grid>
            )}
          </Grid>
        ))}

        <Box display="flex" justifyContent="flex-start" mt={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon fontSize="small" />}
            onClick={addNewAction}
          >
            Add Action
          </Button>
        </Box>
      </InnerShadowBox>

      <Divider sx={{ my: 2, borderColor: '#e5e7eb' }} />

      <Box display="flex" justifyContent="flex-end" gap={1.5} paddingX={2} paddingY={2}>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          disabled={isBusy}
          onClick={handleSubmit(handleSubmitShipmentActions)}
        >
          Create Actions
        </Button>
      </Box>
    </LoadingWrapper>
  );
};

export default ShipmentActionBulkForm;